/**
 * RolesTab - CRUD de los roles de la plataforma.
 *
 * Antes esto era solo un editor de matriz con un <select> de roles; ahora la
 * pantalla es el gestionador completo: listado, alta, edicion y borrado.
 *
 * Reglas que la UI refleja pero NO decide (el API las vuelve a aplicar):
 *  - Crear/editar/borrar roles es solo para personal interno (`internal`). Un
 *    titular de cuenta (`super`) es comodin y tiene users.manage_roles, asi que
 *    si la UI se guiara por ese permiso, dejaria editar la tabla global
 *    role_presets y cambiar los permisos de todos los tenants.
 *  - `admin` y `super` vienen marcados `editable: false` porque su semantica esta
 *    en el codigo (comodines). Se muestran bloqueados, con candado, en vez de
 *    ocultos, para que se entienda por que.
 *  - El borrado se rechaza si el rol tiene usuarios; el API lo dice y aqui se
 *    muestra el motivo.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLock, faPlus, faShieldHalved, faSync } from '@fortawesome/free-solid-svg-icons';
import AccountsUsersService from '../../../services/AccountsUsersService';
import usePermissions from '../../../hooks/usePermissions';
import { Loading } from '../../Loading';
import ConfirmationModal from '../../ConfirmationModal';
import { RoleTable } from './RoleTable';
import { RoleModal } from './RoleModal';
import { CODE_MAX, CODE_RE, NAME_MAX, countChosenOn, normalise } from './roleMatrix';

export function RolesTab() {
    const { can, internal, loading: permsLoading, error: permsError } = usePermissions();
    // manage_roles habilita la lectura de la lista (los selectores de rol la
    // necesitan). Crear/editar/borrar exige ademas ser interno.
    const canRead = can('users', 'manage_roles') || can('users', 'assign_role');
    const canManage = can('users', 'manage_roles') && internal;

    const [catalog, setCatalog] = useState([]);
    const [presets, setPresets] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [feedback, setFeedback] = useState(null);

    // Estado del modal de alta/edicion. `preset` null = alta.
    const [modal, setModal] = useState(null);

    // Confirmaciones reutilizadas: borrado y descarte de cambios sin guardar.
    const [confirm, setConfirm] = useState(null);
    const [saving, setSaving] = useState(false);

    const flash = useCallback((type, msg) => {
        setFeedback({ type, msg });
        setTimeout(() => setFeedback(null), 4000);
    }, []);

    // Catalogo + presets juntos: la matriz no se puede dibujar sin ambos.
    const load = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const [cat, pre] = await Promise.all([
                AccountsUsersService.getPermissionsCatalog(),
                AccountsUsersService.getRolePresets(),
            ]);
            setCatalog(cat.catalog || []);
            setPresets(pre.items || []);
            return pre.items || [];
        } catch (e) {
            setError(e.message || 'No se pudo cargar el catalogo de permisos');
            return null;
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    // ---- modal ----------------------------------------------------------------

    const openCreate = () => {
        setModal({
            preset: null,
            code: '',
            name: '',
            description: '',
            draft: normalise({}, catalog),
            fieldErrors: {},
        });
    };

    const openEdit = (preset) => {
        if (!preset.editable) return;
        setModal({
            preset,
            // El codigo no se renombra: users.type lo referencia. Renombrarlo
            // dejaria usuarios apuntando a un rol inexistente.
            code: preset.code,
            name: preset.name || '',
            description: preset.description || '',
            draft: normalise(preset.modules, catalog),
            fieldErrors: {},
        });
    };

    const changeField = (field, value) => {
        setModal((prev) => ({
            ...prev,
            [field]: value,
            fieldErrors: { ...prev.fieldErrors, [field]: null },
        }));
    };

    const setAction = (module, action, value) => {
        setModal((prev) => ({
            ...prev,
            draft: {
                ...prev.draft,
                [module]: { ...(prev.draft[module] || {}), [action]: value ? 1 : 0 },
            },
        }));
    };

    // Quitar todas no puede apagar lo que el backend concede siempre: el servidor
    // lo volvería a poner al guardar y la UI quedaría mintiendo. Quien llama
    // (PermissionPicker) ya deja fuera las acciones forzadas de `actionCodes`.
    const setModule = (module, actionCodes, value) => {
        setModal((prev) => {
            const next = { ...(prev.draft[module] || {}) };
            actionCodes.forEach((a) => {
                next[a] = value ? 1 : 0;
            });
            return { ...prev, draft: { ...prev.draft, [module]: next } };
        });
    };

    const dirty = useMemo(() => {
        if (!modal) return false;
        if (!modal.preset) {
            // countChosenOn y no countOn: biblio.view viene concedido en cualquier
            // alta, asi que contar tambien lo forzado haria pedir confirmacion de
            // descarte en un modal recien abierto.
            return Boolean(
                modal.code || modal.name || modal.description
                || countChosenOn(modal.draft, catalog) > 0
            );
        }
        return (
            modal.name !== (modal.preset.name || '')
            || modal.description !== (modal.preset.description || '')
            || JSON.stringify(modal.draft) !== JSON.stringify(normalise(modal.preset.modules, catalog))
        );
    }, [modal, catalog]);

    // Un clic fuera o en la X con cambios sin guardar no debe perder la matriz.
    const requestClose = () => {
        if (saving) return;
        if (dirty) {
            setConfirm({ kind: 'discard' });
            return;
        }
        setModal(null);
    };

    const validate = () => {
        const errs = {};
        if (!modal.preset) {
            if (!modal.code.trim()) {
                errs.code = 'El codigo es requerido';
            } else if (!CODE_RE.test(modal.code.trim())) {
                errs.code = 'Solo minusculas, digitos y guion bajo; debe empezar por letra';
            } else if (modal.code.trim().length > CODE_MAX) {
                errs.code = `Maximo ${CODE_MAX} caracteres`;
            }
        }
        if (!modal.name.trim()) {
            errs.name = 'El nombre es requerido';
        } else if (modal.name.trim().length > NAME_MAX) {
            errs.name = `Maximo ${NAME_MAX} caracteres`;
        }
        setModal((prev) => ({ ...prev, fieldErrors: errs }));
        return Object.keys(errs).length === 0;
    };

    const handleSave = async () => {
        if (!canManage || saving) return;
        if (!validate()) return;
        const code = modal.preset ? modal.preset.code : modal.code.trim();
        const name = modal.name.trim();
        const wasNew = !modal.preset;
        setSaving(true);
        try {
            const res = await AccountsUsersService.saveRolePreset({
                code,
                name,
                description: modal.description.trim(),
                modules: normalise(modal.draft, catalog),
            });
            setModal(null);
            const items = await load();
            flash(
                'success',
                `Rol "${name}" ${wasNew ? 'creado' : 'actualizado'}`
                + (res && res.affected_users
                    ? `. ${res.affected_users} usuario(s) ya lo tenian: su matriz individual tiene prioridad y no cambia.`
                    : '')
            );
            // Si se creo uno nuevo, abrirlo deja el flujo continuo; en edicion no
            // se reabre para no devolver al usuario a donde ya estaba.
            if (wasNew && items) {
                const fresh = items.find((r) => r.code === code);
                if (fresh && fresh.editable) openEdit(fresh);
            }
        } catch (e) {
            flash('error', e.message || 'No se pudo guardar el rol');
        } finally {
            setSaving(false);
        }
    };

    // ---- borrado --------------------------------------------------------------

    const doDelete = async (preset) => {
        setSaving(true);
        try {
            await AccountsUsersService.deleteRole(preset.code);
            setConfirm(null);
            if (modal && modal.preset && modal.preset.code === preset.code) setModal(null);
            await load();
            flash('success', `Rol "${preset.name}" eliminado`);
        } catch (e) {
            setConfirm(null);
            flash('error', e.message || 'No se pudo eliminar el rol');
        } finally {
            setSaving(false);
        }
    };

    // ---- render ---------------------------------------------------------------

    if (permsLoading) return <Loading />;

    if (permsError) {
        return <div className="alert alert-danger py-2">{permsError}</div>;
    }

    if (!canRead) {
        return (
            <div className="filters-panel text-center py-4">
                <FontAwesomeIcon icon={faLock} className="me-2" />
                No tienes permiso para ver los roles.
            </div>
        );
    }

    const totalActions = catalog.reduce((n, m) => n + m.actions.length, 0);

    return (
        <>
            {error && (
                <div className="alert alert-danger py-2">{error}
                    <button className="btn-close ms-2 float-end" onClick={load} />
                </div>
            )}
            {feedback && (
                <div className={`alert alert-${feedback.type === 'success' ? 'success' : 'danger'} py-2`}>
                    {feedback.msg}
                    <button className="btn-close ms-2 float-end" onClick={() => setFeedback(null)} />
                </div>
            )}

            {!canManage && (
                <div className="alert alert-warning py-2">
                    <FontAwesomeIcon icon={faShieldHalved} className="me-2" />
                    Puedes consultar los roles, pero no crearlos ni modificarlos: esa accion es exclusiva del
                    personal interno de Ink It. Los roles afectan a todas las cuentas, asi que un titular no
                    debe poder redefinirlos.
                </div>
            )}

            <div className="row mb-3 mt-4">
                <div className="col-md-12 d-flex justify-content-between align-items-center">
                    <span className="fw-bold small text-uppercase text-white opacity-75">
                        {presets.length} rol(es) · {totalActions} acciones
                    </span>
                    {canManage && (
                        <div className="d-flex gap-2">
                            <button
                                className="btn btn-ink-icon"
                                onClick={load}
                                title="Recargar lista"
                            >
                                <FontAwesomeIcon icon={faSync} spin={loading} />
                            </button>
                            <button className="btn btn-success px-3" onClick={openCreate} title="Agregar rol">
                                <FontAwesomeIcon icon={faPlus} className="me-1" /> Agregar
                            </button>
                        </div>
                    )}
                </div>
            </div>

            {loading ? <Loading /> : (
                <RoleTable
                    roles={presets}
                    catalog={catalog}
                    totalActions={totalActions}
                    canManage={canManage}
                    onEdit={openEdit}
                    onDelete={(p) => setConfirm({ kind: 'delete', preset: p })}
                />
            )}

            {modal && (
                <RoleModal
                    isOpen
                    onClose={requestClose}
                    isNew={!modal.preset}
                    code={modal.code}
                    name={modal.name}
                    description={modal.description}
                    draft={modal.draft}
                    fieldErrors={modal.fieldErrors}
                    catalog={catalog}
                    totalActions={totalActions}
                    canSave={canManage && (modal.preset ? modal.preset.editable : true)}
                    saving={saving}
                    onChange={changeField}
                    onSetAction={setAction}
                    onSetModule={setModule}
                    onSave={handleSave}
                />
            )}

            <ConfirmationModal
                isOpen={Boolean(confirm && confirm.kind === 'delete')}
                onClose={() => !saving && setConfirm(null)}
                onConfirm={() => confirm && doDelete(confirm.preset)}
                type="danger"
                isLoading={saving}
                title="Eliminar rol"
                message={
                    confirm && confirm.preset
                        ? `Se eliminará "${confirm.preset.name}". Si tiene usuarios asignados el API lo rechazará y te dirá quiénes.`
                        : ''
                }
                confirmText="Eliminar"
            />

            <ConfirmationModal
                isOpen={Boolean(confirm && confirm.kind === 'discard')}
                onClose={() => setConfirm(null)}
                onConfirm={() => {
                    setConfirm(null);
                    setModal(null);
                }}
                type="warning"
                title="Descartar cambios"
                message="Hay cambios sin guardar en este rol. Si sales ahora se perderán."
                confirmText="Descartar"
            />
        </>
    );
}

export default RolesTab;
